import { IJobDetail } from '@/types/jobs';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const NEW_JOB_THRESHOLD_HOURS = 48;

export const isNewJob = (createdAt: string): boolean =>
  dayjs().diff(dayjs(createdAt), 'hour') < NEW_JOB_THRESHOLD_HOURS;

export const getPostedAgo = (createdAt: string): string => `Posted ${dayjs(createdAt).fromNow()}`;

const SUBSPECIALITY_PREFIX_SEPARATOR = ' - ';

// Subspecialities that are prefixed with an abbreviation of their speciality rather than its full
// name, keyed by the lowercased prefix. Add to this if Salesforce introduces another one.
const SPECIALITY_PREFIX_ALIASES: Record<string, string> = {
  gp: 'general practice (gp)',
  'o & g': 'obstetrics and gynaecology',
};

// Many subspecialities repeat their speciality as a prefix (e.g. "Surgery - Urology" under
// "Surgery"), which would read "Surgery - Surgery - Urology" in the title. Drop the prefix only
// when it names the job's own speciality, so a prefix that adds information is kept.
const removeSpecialityPrefix = (subspeciality: string, speciality?: string): string => {
  const separatorIndex = subspeciality.indexOf(SUBSPECIALITY_PREFIX_SEPARATOR);
  if (separatorIndex === -1 || !speciality) {
    return subspeciality;
  }
  const prefix = subspeciality.slice(0, separatorIndex).trim().toLowerCase();
  const normalisedSpeciality = speciality.trim().toLowerCase();
  const prefixMatchesSpeciality =
    prefix === normalisedSpeciality || SPECIALITY_PREFIX_ALIASES[prefix] === normalisedSpeciality;
  return prefixMatchesSpeciality
    ? subspeciality.slice(separatorIndex + SUBSPECIALITY_PREFIX_SEPARATOR.length)
    : subspeciality;
};

// Built from the structured fields rather than the job's free-text title, which recruiters often
// write as just "Grade - Medicine" and so hides the subspecialty the job is actually for.
export const getJobDisplayTitle = ({
  grade,
  speciality,
  subspeciality,
}: Pick<IJobDetail, 'grade' | 'speciality' | 'subspeciality'>): string =>
  [grade, speciality, subspeciality && removeSpecialityPrefix(subspeciality, speciality)]
    .filter(Boolean)
    .join(' - ');

// Marks the point right after sentence-ending punctuation + whitespace, so the string can be
// split there without needing lookbehind (`(?<=...)`), which throws a SyntaxError at
// script-parse time on Safari/iOS below 16.4 - taking down the whole bundle this ships in, not
// just this function. Uses a control character that can't otherwise appear in job description
// text as the split point.
const SENTENCE_BOUNDARY = '\u0000';

export const getJobHighlights = (text?: string, max = 3): string[] => {
  if (!text) {
    return [];
  }
  return text
    .replace(/([.!?])\s+/g, `$1${SENTENCE_BOUNDARY}`)
    .split(SENTENCE_BOUNDARY)
    .map(sentence => sentence.trim())
    .filter(Boolean)
    .slice(0, max);
};
